import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import pciSealFallback from "@/assets/complisassure-secured-aperia-seal.png";

const TRUST_MESSAGE =
  "Payments for Dosevana orders are handled through Dosevana's payment process. MyGLP1Compare does not store payment information. All medication requests require independent provider review and approval before treatment can be prescribed or shipped.";

const TrustComplianceBlock = ({
  useLiveSeal = false,
  title = "Secure checkout",
  containerClassName = "",
  messageClassName = "",
  message = TRUST_MESSAGE,
  allowLongMessage = true
}) => {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith("/admin");
  const [isSealReady, setIsSealReady] = useState(false);
  const sealTimerRef = useRef(null);
  const sealContainerRef = useRef(null);

  useEffect(() => {
    const container = sealContainerRef.current;
    if (!container) return undefined;

    if (isAdminRoute || !useLiveSeal) {
      container.innerHTML = "";
      setIsSealReady(false);
      return undefined;
    }

    if (container.querySelector("img")) {
      setIsSealReady(true);
      return undefined;
    }

    const originalWrite = document.write.bind(document);
    document.write = (content) => {
      container.innerHTML = content;
    };

    const script = document.createElement("script");
    script.id = "rapidscansecure-seal";
    script.src = "https://rapidscansecure.com/siteseal/siteseal.js?code=17,31CEE69B37A2FF39D945507CAD7F32C1E2059340";
    script.async = true;
    script.defer = true;
    script.onload = () => {
      sealTimerRef.current = window.setTimeout(() => {
        setIsSealReady(Boolean(container.querySelector("img")));
        document.write = originalWrite;
      }, 120);
    };
    script.onerror = () => {
      document.write = originalWrite;
      setIsSealReady(false);
    };
    container.appendChild(script);

    return () => {
      document.write = originalWrite;
      if (sealTimerRef.current) {
        window.clearTimeout(sealTimerRef.current);
      }
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, [isAdminRoute, useLiveSeal]);

  if (isAdminRoute) {
    return null;
  }

  const safeMessage = message || TRUST_MESSAGE;
  const shortMessage = safeMessage.split(".")[0] + ".";

  return (
    <div
      className={`bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-4 ${containerClassName}`}
      data-testid="trust-compliance-block"
    >
      <div>
        <h3 className="text-sm font-semibold text-[#2D3748] mb-2" data-testid="trust-compliance-title">
          {title}
        </h3>
        <p className={`text-xs text-[#718096] max-w-2xl ${messageClassName}`} data-testid="trust-compliance-message">
          {allowLongMessage ? safeMessage : shortMessage}
        </p>
      </div>
      <div className="flex flex-col items-start gap-2" data-testid="trust-compliance-seal">
        <div
          className="pci-seal min-h-[60px] w-full max-w-[180px]"
          ref={sealContainerRef}
          data-testid="trust-compliance-seal-live"
        />
        {!useLiveSeal && (
          <img
            src={pciSealFallback}
            alt="CompliAssure Secured powered by Aperia"
            className="max-w-[180px] w-full h-auto"
            loading="lazy"
            data-testid="trust-compliance-seal-fallback"
          />
        )}
        {useLiveSeal && !isSealReady && (
          <img
            src={pciSealFallback}
            alt="CompliAssure Secured powered by Aperia"
            className="max-w-[180px] w-full h-auto"
            loading="lazy"
            data-testid="trust-compliance-seal-fallback-live"
          />
        )}
      </div>
    </div>
  );
};

export default TrustComplianceBlock;
